import { BaseComponent } from 'components/BaseComponent';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { HBox, VBox } from '../../../components/Box';
import { Check, Times } from '../../../components/Icon';
import { Modal, ModalControls } from '../../../components/Modal';
import { FormField, FormFields } from '../../../components/FormFields';
import { Input } from '../../../components/Input';
import { Button } from '../../../components/Buttons';
import { deleteUserProperty, fetchCountries, fetchUserPropertyNames, setUserProperty } from '../actions';
import _ from 'lodash';
import { isNumeric } from '../../../utils/string';
import { selectUserPropertyNames } from '../selectors';
import { Dropdown } from '../../../components/Dropdown';

const STATE = {
  edit: null,
  value: '',
  add: false,
  addValue: '',
  deletePropertyConfirm: null,
};

const Container = styled.div`
  .user-property-row {
    > div {
      width: 200px;

      &:last-child {
        width: 100px;
        text-align: right;
      }
    }
  }
`;

const parsePropertyValue = function(value) {
  //todo: move parsing to API
  //basic parsing ... (should probably be done at api level?)

  if (value === 'false') value = false;
  if (value === 'true') value = true;
  if (isNumeric(value)) value = parseFloat(value);
  return value;
};

const Property = styled.div`
  > .box {
    align-items: center;
    border-bottom: 1px solid #ddd;
  }
  .name {
    margin-right: 12px;
    width: 200px;
  }
`;

/* eslint-disable react/prefer-stateless-function */
class UserPropertiesEditorComp extends BaseComponent {
  constructor(props) {
    super(props, { ...STATE });
  }

  componentDidMount() {
    this.props.fetch();
  }

  editProperty(property) {
    this.setState({ edit: property.id, value: property.value ?? '' });
  }

  reset() {
    this.setState({ ...STATE });
  }

  get user() {
    return this.props.user;
  }

  save() {
    let value = parsePropertyValue(this.state.value);

    this.props.save(this.user.properties.find(p => p.id === this.state.edit).name.name, value, this.reset.bind(this));
  }

  add() {
    const name = this.props.userPropertyNames.find(p => p.id === this.state.addPropertyNameId).name;
    let value = parsePropertyValue(this.state.addValue);

    this.props.save(name, value, this.reset.bind(this));
  }

  render() {
    const user = this.props.user;
    if (!user) return null;
    return (
      <Container>
        {this.state.add ? this.renderAdd() : null}
        {this.renderDeleteProperty()}
        <h3>{this.lbl('user.details.properties')}:</h3>
        <div>
          <Button small onClick={this.setStateFn({ add: true })}>
            +
          </Button>
        </div>
        <VBox>{user.properties.map(p => this.renderProperty(p, user.id))}</VBox>
      </Container>
    );
  }

  renderAdd() {
    const options = this.props.userPropertyNames?.map(prop => ({ label: prop.name, value: prop.id }));
    const selected = options.find(o => o.value == this.state.addPropertyNameId);
    if (!options || !options.length) return null;
    return (
      <Modal onClose={this.reset.bind(this)} title={this.lbl('user.property.add')}>
        <Dropdown options={options} onChange={o => this.setState({ addPropertyNameId: o.value })} value={selected} />
        {selected ? (
          <FormField>
            <Input name="addValue" value={this.state.addValue} onChange={this.onInputChange.bind(this)} />
          </FormField>
        ) : null}
        <ModalControls>
          <Button primary disabled={!selected} onClick={this.add.bind(this)}>
            Save
          </Button>
          <Button onClick={this.reset.bind(this)}>{this.lbl('cancel')}</Button>
        </ModalControls>
      </Modal>
    );
  }

  renderProperty(property, userId) {
    const value = property.value;

    if (this.state.edit === property.id) return this.renderEditProperty(property);
    return (
      <Property key={`${property.id}`} onClick={() => this.editProperty(property)}>
        <HBox className="user-property-row">
          <div className="name">{property.name.name}</div>
          <div className="value">{value === true ? <Check /> : value === false ? <Times /> : value}</div>
          <div className="name">
            <Button small onClick={this.setStateFn({ deletePropertyConfirm: { propertyId: property.id, userId } })}>
              <Times />
            </Button>
          </div>
        </HBox>
      </Property>
    );
  }

  renderEditProperty(property) {
    return (
      <Modal key={property.id} onClose={this.reset.bind(this)} title={this.lbl('user.property.edit')}>
        <FormFields>
          <h1>{property.name.name}</h1>
          <FormField>
            <Input value={this.state.value} name="value" onChange={this.onInputChange.bind(this)} />
          </FormField>
        </FormFields>
        <ModalControls>
          <Button primary onClick={this.save.bind(this)}>
            Save
          </Button>
          <Button onClick={this.reset.bind(this)}>{this.lbl('cancel')}</Button>
        </ModalControls>
      </Modal>
    );
  }

  renderDeleteProperty() {
    if (!this.state.deletePropertyConfirm) return null;
    return (
      <Modal onClose={this.reset.bind(this)} title={this.lbl('user.modal.delete.confirm.title')}>
        <ModalControls>
          <Button
            isPrimary
            onClick={() => {
              this.props.deleteUserProperty(
                this.state.deletePropertyConfirm.propertyId,
                this.state.deletePropertyConfirm.userId,
                this.reset.bind(this),
              );
            }}
          >
            {this.lbl('confirm')}
          </Button>
          <Button onClick={this.reset.bind(this)}>{this.lbl('cancel')}</Button>
        </ModalControls>
      </Modal>
    );
  }
}

UserPropertiesEditorComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    userPropertyNames: selectUserPropertyNames(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
    fetch() {
      dispatch(fetchUserPropertyNames());
    },
    save(property_name, value, cb) {
      dispatch(setUserProperty(ownProps.user.id, property_name, value, cb));
    },
    deleteUserProperty(propertyId, userId, callback) {
      dispatch(deleteUserProperty(propertyId, userId, callback));
    },
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export const UserPropertiesEditor = compose(
  withConnect,
  withRouter,
)(UserPropertiesEditorComp);
